import {
  getApiErrorUserMessage,
  readApiError,
  type ApiErrorResult,
} from "_shared/_errors/api-error";
import type { CalendarEventFormErrors, CalendarEventFormState } from "calendar/calendar-event-form";

export type CalendarEventFailure = {
  apiError: ApiErrorResult;
  fieldErrors: CalendarEventFormErrors;
  message?: string;
};

const fieldMap: Readonly<Record<string, keyof CalendarEventFormState>> = {
  title: "title",
  startdate: "startDate",
  enddate: "endDate",
  start: "startDateTime",
  end: "endDateTime",
  timezoneid: "timeZoneId",
  description: "description",
  link: "link",
  "recurrence.frequency": "frequency",
  recurrencefrequency: "frequency",
  "recurrence.interval": "interval",
  recurrenceinterval: "interval",
  "recurrence.weekdays": "weekdays",
  recurrenceweekdays: "weekdays",
  "recurrence.count": "count",
  recurrencecount: "count",
  "recurrence.untildate": "untilDate",
  recurrenceuntildate: "untilDate",
  "recurrence.until": "untilDateTime",
  recurrenceuntil: "untilDateTime",
};

/** Converts the Calendar wire contract into editor fields without rendering untrusted response text. */
export function readCalendarEventFailure(error: unknown): CalendarEventFailure {
  const apiError = readApiError(error);
  const fieldErrors: CalendarEventFormErrors = {};
  const nonFieldMessages: string[] = [];

  for (const [wireField, messages] of Object.entries(apiError.trusted ? apiError.fieldErrors : {})) {
    const formField = fieldMap[wireField.toLowerCase()];
    const message = messages.find((candidate) => candidate.trim().length > 0);
    if (!message) continue;
    if (formField) fieldErrors[formField] ??= message;
    else nonFieldMessages.push(message);
  }

  const trustedMessage = apiError.trusted
    ? combineProblemText(apiError.title, apiError.detail)
    : getApiErrorUserMessage(apiError);

  return {
    apiError,
    fieldErrors,
    message: nonFieldMessages[0] ?? (Object.keys(fieldErrors).length === 0 ? trustedMessage : undefined),
  };
}

function combineProblemText(title: string | undefined, detail: string | undefined): string | undefined {
  const parts = [title?.trim(), detail?.trim()].filter((value): value is string => Boolean(value));
  return [...new Set(parts)].join(" ") || undefined;
}
